import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router-dom";

import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { Printer } from '../../models/interfaces';
import { PrintersContext } from '../../context/printers/PrinterContext';


const PAGE_SIZE = 10;

const locationConfig: Record<number, { label: string; icon: string; badgeClass: string }> = {
    1: { label: 'Cocina', icon: 'bi-fire', badgeClass: 'bg-warning-subtle text-warning-emphasis' },
    2: { label: 'Cajas', icon: 'bi-cash-stack', badgeClass: 'bg-info-subtle text-info-emphasis' },
};

// Igual que antes: todo lo que no sea cocina se considera cajas
const getLocation = (printer: Printer) => locationConfig[printer.typeId == 1 ? 1 : 2];

export const PrintersPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const [activeLocation, setActiveLocation] = useState<'all' | 1 | 2>('all');
    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState('');
    const [printerId, setPrinterId] = useState<number | null>(null);

    const { printers, loadPrinters, deletePrinter } = useContext(PrintersContext);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                await loadPrinters(0);
            } catch (e) {
                setError('No se pudieron cargar las impresoras, intente de nuevo.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [])

    useEffect(() => {
        setPageActual(1);
    }, [buscar, activeLocation])

    const counts = useMemo(() => ({
        all: printers.length,
        1: printers.filter(p => p.typeId == 1).length,
        2: printers.filter(p => p.typeId != 1).length,
    }), [printers]);

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        return printers
            .filter(p => activeLocation === 'all' || (activeLocation === 1 ? p.typeId == 1 : p.typeId != 1))
            .filter(p => !term ||
                String(p.name ?? '').toLowerCase().includes(term) ||
                String(p.ip ?? '').toLowerCase().includes(term));
    }, [printers, buscar, activeLocation]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);

    // Si al eliminar se reduce el número de páginas, se ajusta la página actual
    useEffect(() => {
        if (pageActual > totalPage) setPageActual(totalPage);
    }, [totalPage])

    const handleNew = () => {
        navigate("/printer");
    }

    const handleEdit = (printer: Printer) => {
        navigate("/printer", { state: { printer } });
    }

    const handleDelete = (printer: Printer) => {
        setPrinterId(printer.id);
        setConfirm(`¿Está seguro que desea eliminar la impresora "${printer.name}"?`);
    }

    const processDelete = async () => {
        setConfirm('');
        if (printerId === null) return;
        const resp = await deletePrinter(printerId);
        if (resp !== "success") {
            setError(resp);
        }
        setPrinterId(null);
    }

    const tabs: { key: 'all' | 1 | 2; label: string }[] = [
        { key: 'all', label: 'Todas' },
        { key: 1, label: 'Cocina' },
        { key: 2, label: 'Cajas' },
    ];

    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const removeLocalError = () => setError('');
    const removeConfirmMessage = () => { setConfirm(''); setPrinterId(null); };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Impresoras</h3>
                    <p className="text-muted mb-0">Configure las impresoras de cocina y cajas de su comercio.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar impresora
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    {/* Tabs de ubicación */}
                    <ul className="nav nav-tabs mb-3">
                        {tabs.map(tab => (
                            <li className="nav-item" key={tab.key}>
                                <button
                                    className={`nav-link ${activeLocation === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveLocation(tab.key)}
                                >
                                    {tab.label} ({counts[tab.key]})
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por nombre o IP..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table id='printerTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Nombre</th>
                                    <th>Dirección IP</th>
                                    <th>Ubicación</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={4} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : pageItems.length > 0 ? (
                                    pageItems.map(printer => {
                                        const location = getLocation(printer);
                                        return (
                                            <tr key={printer.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div style={styles.iconCircle}>
                                                            <i className="bi bi-printer" />
                                                        </div>
                                                        <span className="fw-medium">{printer.name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-muted" style={{ fontFamily: 'monospace' }}>{printer.ip || '-'}</td>
                                                <td>
                                                    <span className={`badge rounded-pill ${location.badgeClass}`}>
                                                        <i className={`bi ${location.icon} me-1`} />{location.label}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center', width: 110 }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        title="Editar impresora"
                                                        onClick={() => handleEdit(printer)}
                                                    >
                                                        <i className="bi bi-pencil" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger ms-1"
                                                        title="Eliminar impresora"
                                                        onClick={() => handleDelete(printer)}
                                                    >
                                                        <i className="bi bi-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr><td colSpan={4} className='text-center py-4 text-muted'>
                                        {buscar || activeLocation !== 'all' ? 'No se encontraron impresoras para el filtro seleccionado.' : 'No hay impresoras configuradas.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="text-muted small">
                            {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} impresoras` : ''}
                        </span>
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message
                    type="error"
                    message={error}
                    closeDialog={removeLocalError}
                    handleOk={removeLocalError}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {confirm.length > 0 && (
                <Message
                    type="success"
                    title="Atención"
                    message={confirm}
                    closeDialog={removeConfirmMessage}
                    showCancel={true}
                    handleCancel={removeConfirmMessage}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    iconCircle: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e7f1ff',
        color: '#0d6efd',
        flexShrink: 0,
    },
};
